import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { 
  Flame, 
  Droplets, 
  Settings, 
  Phone, 
  Mail, 
  MapPin,
  Menu,
  X,
  ChevronRight,
  Clock,
  Truck,
  Shield,
  Package,
  Smartphone,
  RefreshCw
} from "lucide-react";

const LOGO_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_summary-hub-95/artifacts/v1xz43s3_Gasman%20Logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/uploads/")) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const formatPrice = (price) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(price);

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(false);

  const fetchProducts = async () => {
    setProductsLoading(true);
    setProductsError(false);
    try {
      const response = await axios.get(`${API}/public/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Public products fetch error", error);
      setProductsError(true);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="GasMan4U" className="h-10 w-auto" />
              <span className="text-xl font-bold text-gray-900">GasMan4U</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection("hero")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-home"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("products")}
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-products"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection("services")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-services"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection("about")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-about"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection("contact")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-contact"
              >
                Contact
              </button>
              <Link
                to="/get-app"
                className="inline-flex items-center gap-2 text-[#0A4CAE] hover:text-[#F7851E] transition-colors font-semibold"
                data-testid="nav-download-app"
              >
                <Smartphone size={18} /> Download App
              </Link>
              <Link to="/admin/login">
                <Button 
                  className="bg-[#0A4CAE] text-white hover:bg-[#F7851E] rounded-full px-6 glow-primary"
                  data-testid="nav-admin-login"
                >
                  Admin Login
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button 
                onClick={() => scrollToSection("hero")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("products")}
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection("services")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection("about")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection("contact")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Contact
              </button>
              <Link to="/get-app" className="block">
                <Button className="w-full bg-[#F7851E] text-white hover:bg-[#df7415] rounded-full">
                  <Smartphone size={18} className="mr-2" /> Download App
                </Button>
              </Link>
              <Link to="/admin/login" className="block">
                <Button className="w-full bg-[#0A4CAE] text-white hover:bg-[#F7851E] rounded-full">
                  Admin Login
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section pt-24 pb-10 md:pt-28 md:pb-14" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Fast & Reliable
                <span className="text-[#0A4CAE]"> Gas Delivery</span>
                <br />at Your Doorstep
              </h1>
              <p className="text-gray-600 text-lg mb-8 max-w-lg">
                Your trusted partner for gas cylinders, water cans, regulators, and all gas-related products. 
                Quick delivery, quality assured.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/get-app">
                  <Button
                    className="bg-[#0A4CAE] text-white hover:bg-[#F7851E] rounded-full px-8 py-6 text-lg font-semibold glow-primary"
                    data-testid="hero-download-app"
                  >
                    <Smartphone className="mr-2" size={20} /> Download App
                  </Button>
                </Link>
                <Button 
                  onClick={() => scrollToSection("products")}
                  variant="outline"
                  className="border-[#0A4CAE] text-[#0A4CAE] hover:bg-[#0A4CAE]/10 rounded-full px-8 py-6 text-lg"
                  data-testid="hero-cta-secondary"
                >
                  Browse Products
                  <ChevronRight className="ml-2" size={20} />
                </Button>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 mt-12">
                <div className="animate-fade-in-delay-1">
                  <p className="text-3xl font-bold text-[#0A4CAE]">500+</p>
                  <p className="text-gray-500 text-sm">Happy Customers</p>
                </div>
                <div className="animate-fade-in-delay-2">
                  <p className="text-3xl font-bold text-[#0A4CAE]">24/7</p>
                  <p className="text-gray-500 text-sm">Support Available</p>
                </div>
                <div className="animate-fade-in-delay-3">
                  <p className="text-3xl font-bold text-[#0A4CAE]">30min</p>
                  <p className="text-gray-500 text-sm">Avg. Delivery</p>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-delay-2">
              <div className="absolute inset-0 bg-[#0A4CAE]/20 blur-3xl rounded-full"></div>
              <img 
                src={LOGO_URL} 
                alt="GasMan4U Delivery" 
                className="relative z-10 w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="pt-7 pb-20 md:pt-8 bg-[#f8fafc]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="h-px max-w-5xl mx-auto mb-8 md:mb-10 bg-gradient-to-r from-transparent via-[#0A4CAE]/40 to-transparent"
            aria-hidden="true"
          />
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#0A4CAE]">Services</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide a complete range of gas and water delivery services to meet all your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Gas Cylinders */}
            <div className="service-card group" data-testid="service-gas-cylinders">
              <div className="w-14 h-14 bg-[#0A4CAE]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0A4CAE]/20 transition-colors">
                <Flame className="text-[#0A4CAE]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Gas Cylinders</h3>
              <p className="text-gray-600 mb-4">
                Premium quality LPG cylinders for homes and businesses. Safe, efficient, and delivered on time.
              </p>
              <div className="flex items-center text-[#0A4CAE] text-sm font-semibold">
                Learn more <ChevronRight size={16} className="ml-1" />
              </div>
            </div>

            {/* Water Cans */}
            <div className="service-card group" data-testid="service-water-cans">
              <div className="w-14 h-14 bg-[#0A4CAE]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0A4CAE]/20 transition-colors">
                <Droplets className="text-[#0A4CAE]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Water Cans</h3>
              <p className="text-gray-600 mb-4">
                Pure and clean drinking water delivered in sealed cans. Stay hydrated with our quality water.
              </p>
              <div className="flex items-center text-[#0A4CAE] text-sm font-semibold">
                Learn more <ChevronRight size={16} className="ml-1" />
              </div>
            </div>

            {/* Regulators & Accessories */}
            <div className="service-card group" data-testid="service-regulators">
              <div className="w-14 h-14 bg-[#0A4CAE]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0A4CAE]/20 transition-colors">
                <Settings className="text-[#0A4CAE]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Regulators & Accessories</h3>
              <p className="text-gray-600 mb-4">
                High-quality gas regulators, pipes, and accessories. Safety equipment for your peace of mind.
              </p>
              <div className="flex items-center text-[#0A4CAE] text-sm font-semibold">
                Learn more <ChevronRight size={16} className="ml-1" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <p className="text-sm font-bold tracking-widest uppercase text-[#F7851E] mb-3">Our catalogue</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Products we <span className="text-[#0A4CAE]">deliver</span>
              </h2>
              <p className="text-gray-600 max-w-2xl">
                Browse our live catalogue and prices. To place an order, download the GasMan4U mobile app.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0A4CAE]/10 px-4 py-2 text-sm font-semibold text-[#0A4CAE] self-start sm:self-auto">
              <Smartphone size={17} /> Ordering is available in the app
            </div>
          </div>

          {productsLoading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" aria-label="Loading products">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
                  <div className="h-52 bg-gray-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 w-20 bg-gray-100 rounded" />
                    <div className="h-6 w-3/4 bg-gray-100 rounded" />
                    <div className="h-10 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!productsLoading && productsError && (
            <div className="rounded-2xl border border-gray-200 bg-[#f8fafc] py-14 px-6 text-center">
              <Package className="mx-auto text-gray-400 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">We couldn't load the catalogue</h3>
              <p className="text-gray-500 text-sm mb-5">Please try again in a moment.</p>
              <Button onClick={fetchProducts} variant="outline" className="rounded-full border-[#0A4CAE] text-[#0A4CAE]">
                <RefreshCw size={16} className="mr-2" /> Try again
              </Button>
            </div>
          )}

          {!productsLoading && !productsError && products.length === 0 && (
            <div className="rounded-2xl border border-gray-200 bg-[#f8fafc] py-14 px-6 text-center">
              <Package className="mx-auto text-gray-400 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">New products are coming soon</h3>
              <p className="text-gray-500 text-sm">Check back shortly for our latest catalogue.</p>
            </div>
          )}

          {!productsLoading && !productsError && products.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => {
                const imageUrl = getImageUrl(product.image);
                return (
                  <article key={product.id} className="product-card group" data-testid={`public-product-${product.id}`}>
                    <div className="relative h-52 bg-gradient-to-br from-blue-50 to-slate-100 overflow-hidden">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={product.name}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <Package className="text-[#0A4CAE]/30" size={58} />
                        </div>
                      )}
                      <span className={`absolute top-3 right-3 rounded-full px-3 py-1 text-xs font-bold ${product.in_stock ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                        {product.in_stock ? "In stock" : "Out of stock"}
                      </span>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      {product.category_name && (
                        <p className="text-xs font-bold uppercase tracking-wider text-[#0A4CAE] mb-2">{product.category_name}</p>
                      )}
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed mb-5 line-clamp-2">{product.description}</p>
                      <div className="mt-auto">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <p className="text-xl font-bold text-gray-900">{formatPrice(product.price)}</p>
                          <p className="flex items-center gap-1 text-xs text-gray-500"><Clock size={14} /> {product.avg_delivery_time}</p>
                        </div>
                        <Link
                          to="/get-app"
                          className="block w-full rounded-xl bg-[#0A4CAE] px-4 py-3 text-center text-sm font-semibold text-white hover:bg-[#083d8a] transition-colors"
                        >
                          <Smartphone size={16} className="inline mr-2 -mt-0.5" /> Download app to order
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-[#0A4CAE]">GasMan4U</span>?
              </h2>
              <p className="text-gray-600 mb-8">
                With years of experience in the gas delivery industry, we've built a reputation for 
                reliability, safety, and excellent customer service. Our team is dedicated to providing 
                you with the best products at competitive prices.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0A4CAE]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#0A4CAE]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Quick Delivery</h4>
                    <p className="text-gray-600 text-sm">Average delivery time of 30 minutes within city limits.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0A4CAE]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="text-[#0A4CAE]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Safety First</h4>
                    <p className="text-gray-600 text-sm">All products meet safety standards and are regularly inspected.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0A4CAE]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Truck className="text-[#0A4CAE]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Wide Coverage</h4>
                    <p className="text-gray-600 text-sm">Serving multiple areas with our extensive store location network.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-[#0A4CAE]/10 blur-3xl rounded-full"></div>
              <div className="relative bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
                <img 
                  src={LOGO_URL} 
                  alt="About GasMan4U" 
                  className="w-full max-w-xs mx-auto mb-6"
                />
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#0A4CAE] mb-2">Trusted Since 2020</p>
                  <p className="text-gray-600">Serving thousands of happy customers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#f8fafc]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get in <span className="text-[#0A4CAE]">Touch</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Have questions about our products or service? Contact us through any of the following channels. Orders are placed through the GasMan4U app.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:border-[#0A4CAE] hover:shadow-lg transition-all" data-testid="contact-phone">
              <div className="w-14 h-14 bg-[#0A4CAE]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-[#0A4CAE]" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Phone</h3>
              <a className="block text-gray-600 hover:text-[#0A4CAE]" href="tel:+919066985197">+91 90669 85197</a>
              <a className="block text-gray-600 hover:text-[#0A4CAE]" href="tel:+919844253837">+91 98442 53837</a>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:border-[#0A4CAE] hover:shadow-lg transition-all" data-testid="contact-email">
              <div className="w-14 h-14 bg-[#0A4CAE]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-[#0A4CAE]" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Email</h3>
              <a
                className="text-gray-600 hover:text-[#0A4CAE]"
                href="mailto:support@gasman4u.shop"
              >
                support@gasman4u.shop
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:border-[#0A4CAE] hover:shadow-lg transition-all" data-testid="contact-address">
              <div className="w-14 h-14 bg-[#0A4CAE]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-[#0A4CAE]" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Address</h3>
              <p className="text-gray-600">
                PWD Road, Anjappa Layout, B Narayanapura, Mahadevapura, Bengaluru, Karnataka 560016
              </p>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <iframe
              title="GasMan4U location on Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.693759080563!2d77.68021619999999!3d12.991428599999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae11f5aac814cd%3A0x41b09fd49ca719a2!2sGasMan4U!5e0!3m2!1sen!2sin!4v1788429629755!5m2!1sen!2sin"
              width="100%"
              height="450"
              className="block border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      </section>

      {/* App Download CTA */}
      <section className="bg-white px-4 py-12" aria-labelledby="download-app-heading">
        <div className="max-w-7xl mx-auto overflow-hidden rounded-3xl bg-[#0A4CAE] px-6 py-10 sm:px-10 md:px-14 md:py-12 relative">
          <div className="absolute -top-16 -right-12 h-52 w-52 rounded-full bg-[#F7851E]/25 blur-2xl" aria-hidden="true" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-7">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-widest text-orange-200 mb-2">Ready to order?</p>
              <h2 id="download-app-heading" className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Gas delivery is just a few taps away
              </h2>
              <p className="text-blue-100">
                Download GasMan4U to order products, track deliveries, and manage everything from your phone.
              </p>
            </div>
            <Link to="/get-app" className="self-start md:self-auto flex-shrink-0">
              <Button className="rounded-full bg-white px-7 py-6 text-base font-bold text-[#0A4CAE] hover:bg-orange-50">
                <Smartphone size={20} className="mr-2" /> Download the App
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="GasMan4U" className="h-8 w-auto" />
              <span className="font-bold text-gray-900">GasMan4U</span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 GasMan4U. All rights reserved.
            </p>
            <div className="flex items-center gap-5">
              <Link
                to="/get-app"
                className="text-sm font-semibold text-[#0A4CAE] hover:text-[#F7851E] transition-colors"
              >
                Download App
              </Link>
              <Link
                to="/privacy-policy"
                className="text-sm font-medium text-gray-600 hover:text-[#0A4CAE] transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
